// Filter bar under the top bar: search, status chips, due-date select, colour and owner pickers.
// The filter is viewer-local (never synced); the engine fades the cards that do not match and the
// infobar counts only the matching cards.
import type { DueFilter, FilterSpec, Status } from "./engine-api";
import { isoWeek, localDate } from "./iso";
import { STATUS, STICKY, css } from "./palette";
import { emptyFilter, filterActive, type Store } from "./store";

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};
const button = (cls: string, text?: string) => {
  const b = el("button", cls, text);
  b.type = "button";
  return b;
};

const STATUS_ORDER: Status[] = [1, 2, 4, 3];
type DueKey = "" | "this" | "next" | "overdue" | "mismatch" | "unset";
type Rect = [number, number, number, number];

export class FilterBar {
  private search = $<HTMLInputElement>("#lw-search");
  private due = $<HTMLSelectElement>("#lw-filter-due");
  private colorBtn = $<HTMLButtonElement>("#lw-filter-color");
  private ownerBtn = $<HTMLButtonElement>("#lw-filter-owner");
  private clear = $<HTMLButtonElement>("#lw-filter-clear");
  private showBtn = $<HTMLButtonElement>("#lw-filter-show");
  private result = $("#lw-filter-result");
  private pop = $("#lw-filter-pop");
  private popFor: HTMLButtonElement | null = null;
  private statusChips = new Map<Status, HTMLButtonElement>();
  private dueWeek = 0;

  /**
   * @param show   moves the camera so the given board rect is visible
   * @param release gives the keyboard back to the board
   */
  constructor(private store: Store, private show: (r: Rect) => void, private release: () => void) {
    const group = $("#lw-filter-status");
    for (const s of STATUS_ORDER) {
      const b = button("chip", STATUS[s].label);
      b.style.setProperty("--dot", css(STATUS[s].rgb));
      b.setAttribute("aria-pressed", "false");
      b.addEventListener("click", () => this.update({ statuses: toggle(this.spec.statuses, s) }));
      this.statusChips.set(s, b);
      group.appendChild(b);
    }
    this.fillDueOptions();
    this.search.addEventListener("input", () => this.update({ text: this.search.value }));
    this.search.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") { e.preventDefault(); this.showMatches(); }
      else if (e.key === "Escape") {
        e.preventDefault();
        if (this.search.value) { this.search.value = ""; this.update({ text: "" }); }
        else { this.search.blur(); this.release(); }
      }
    });
    this.due.addEventListener("change", () => this.update({ due: this.dueFilter(this.due.value as DueKey) }));
    // the labels name calendar weeks: rebuild them when the week has changed since the last build
    this.due.addEventListener("focus", () => { if (isoWeek(new Date()).week !== this.dueWeek) this.fillDueOptions(); });
    this.colorBtn.addEventListener("click", () => this.togglePop(this.colorBtn, () => this.colorPop()));
    this.ownerBtn.addEventListener("click", () => this.togglePop(this.ownerBtn, () => this.ownerPop()));
    this.clear.addEventListener("click", () => { this.closePop(); this.set(emptyFilter()); this.release(); });
    this.showBtn.addEventListener("click", () => this.showMatches());
    document.addEventListener("pointerdown", (e) => {
      const t = e.target as Node;
      if (this.popFor && !this.pop.contains(t) && !this.popFor.contains(t)) this.closePop();
    });
    window.addEventListener("keydown", (e) => { if (e.key === "Escape" && this.popFor) this.closePop(); });
    window.addEventListener("resize", () => this.closePop());
    store.subscribe(() => this.refresh());
    this.refresh();
  }

  get spec(): FilterSpec { return this.store.filter; }

  focusSearch() {
    this.search.focus();
    this.search.select();
  }

  private update(p: Partial<FilterSpec>) { this.set({ ...this.spec, ...p }); }

  private set(f: FilterSpec) {
    this.store.setFilter(f);
    if (this.search.value !== f.text) this.search.value = f.text;
  }

  private showMatches() {
    const b = this.store.matchBounds();
    if (b) this.show(b);
  }

  // ---------- due date ----------

  private dueKey(): DueKey {
    const d = this.spec.due;
    if (!d) return "";
    if (d.kind === "week") {
      const now = isoWeek(new Date());
      return d.year === now.year && d.week === now.week ? "this" : "next";
    }
    return d.kind;
  }

  private dueFilter(k: DueKey): DueFilter | null {
    const today = new Date();
    switch (k) {
      case "this": return { kind: "week", ...isoWeek(today) };
      case "next": return { kind: "week", ...isoWeek(new Date(today.getTime() + 7 * 864e5)) };
      case "overdue": return { kind: "overdue", today: localDate(today) };
      case "mismatch": return { kind: "mismatch" };
      case "unset": return { kind: "unset" };
      default: return null;
    }
  }

  private fillDueOptions() {
    const now = isoWeek(new Date());
    this.dueWeek = now.week;
    const next = isoWeek(new Date(Date.now() + 7 * 864e5));
    const opts: [DueKey, string][] = [
      ["", "Fälligkeit: alle"],
      ["this", `Diese Woche (KW ${now.week})`],
      ["next", `Nächste Woche (KW ${next.week})`],
      ["overdue", "Überfällig"],
      ["mismatch", "Liegt in falscher KW"],
      ["unset", "Ohne Fälligkeit"],
    ];
    const cur = this.dueKey();
    this.due.replaceChildren(...opts.map(([v, t]) => {
      const o = el("option", undefined, t);
      o.value = v;
      o.selected = v === cur;
      return o;
    }));
  }

  // ---------- popovers ----------

  private togglePop(btn: HTMLButtonElement, build: () => void) {
    if (this.popFor === btn) return this.closePop();
    this.closePop();
    this.popFor = btn;
    btn.setAttribute("aria-expanded", "true");
    build();
    this.pop.hidden = false;
    const r = btn.getBoundingClientRect();
    const w = this.pop.offsetWidth;
    this.pop.style.transform = `translate(${Math.max(8, Math.min(r.left, window.innerWidth - w - 8))}px, ${r.bottom + 6}px)`;
  }

  private closePop() {
    if (!this.popFor) return;
    this.popFor.setAttribute("aria-expanded", "false");
    this.popFor = null;
    this.pop.hidden = true;
  }

  private colorPop() {
    const pop = this.pop;
    pop.replaceChildren();
    pop.setAttribute("aria-label", "Nach Farbe filtern");
    const sec = el("div", "menu-section");
    sec.appendChild(el("div", "menu-title", "Farbe"));
    const grid = el("div", "swatches");
    STICKY.forEach((c, i) => {
      const b = button("swatch");
      b.title = c.name;
      b.setAttribute("aria-label", c.name);
      b.setAttribute("aria-pressed", String(this.spec.colors.includes(i)));
      b.style.background = css(c.rgb);
      b.addEventListener("click", () => {
        this.update({ colors: toggle(this.spec.colors, i) });
        b.setAttribute("aria-pressed", String(this.spec.colors.includes(i)));
      });
      grid.appendChild(b);
    });
    sec.appendChild(grid);
    pop.appendChild(sec);
    const all = button("menu-action", "Alle Farben");
    all.addEventListener("click", () => { this.update({ colors: [] }); this.closePop(); });
    pop.appendChild(all);
  }

  private ownerPop() {
    const pop = this.pop;
    pop.replaceChildren();
    pop.setAttribute("aria-label", "Nach Person filtern");
    const sec = el("div", "menu-section");
    sec.appendChild(el("div", "menu-title", "Verantwortlich"));
    const row = el("div", "menu-row");
    const owners = this.store.owners();
    for (const o of [...owners, ""]) {
      const b = button("chip owner-chip", o || "Ohne Person");
      b.setAttribute("aria-pressed", String(this.spec.owners.includes(o)));
      b.addEventListener("click", () => {
        this.update({ owners: toggle(this.spec.owners, o) });
        b.setAttribute("aria-pressed", String(this.spec.owners.includes(o)));
      });
      row.appendChild(b);
    }
    sec.appendChild(row);
    if (!owners.length) sec.appendChild(el("div", "menu-hint", "Noch keine Karte hat eine verantwortliche Person (Rechtsklick auf eine Karte → Kürzel)."));
    pop.appendChild(sec);
    const all = button("menu-action", "Alle Personen");
    all.addEventListener("click", () => { this.update({ owners: [] }); this.closePop(); });
    pop.appendChild(all);
  }

  // ---------- state → DOM ----------

  private refresh() {
    const f = this.spec;
    for (const [s, b] of this.statusChips) b.setAttribute("aria-pressed", String(f.statuses.includes(s)));
    const key = this.dueKey();
    if (this.due.value !== key) this.due.value = key;
    this.due.classList.toggle("active", key !== "");
    label(this.colorBtn, "Farbe", f.colors.length);
    label(this.ownerBtn, "Person", f.owners.length);
    const active = filterActive(f);
    this.clear.disabled = !active;
    this.showBtn.hidden = !active;
    if (active) {
      const c = this.store.counts();
      const hits = c.open + c.done + c.waiting + c.cancelled;
      const total = this.store.cardCount();
      this.result.textContent = `${hits} von ${total} ${total === 1 ? "Karte" : "Karten"}`;
      this.showBtn.disabled = hits === 0;
    } else this.result.textContent = "";
  }
}

function toggle<T>(list: T[], v: T): T[] {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

function label(b: HTMLButtonElement, text: string, n: number) {
  b.textContent = text;
  if (n) b.appendChild(el("span", "badge", String(n)));
  if (n) b.dataset.active = ""; else delete b.dataset.active;
}
